import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { StepRun } from '@/types/stepRun';
import type { AutomationRun, ReleaseStepType } from '@/types';
import { pickLatestStepRun } from './stepRun';

interface WorkflowRunStepRuns {
    stepRunsById: Record<string, StepRun>;
    stepGroupAutomationRunsByArchetype: Partial<Record<ReleaseStepType, AutomationRun[]>>;
}

interface StepRunsSummaryState {
    byWorkflowRunId: Record<string, WorkflowRunStepRuns>;
}

const initialState: StepRunsSummaryState = {
    byWorkflowRunId: {},
};

const stepRunsSummarySlice = createSlice({
    name: 'stepRunsSummary',
    initialState,
    reducers: {
        upsertStepRuns(state, action: PayloadAction<{ workflowRunId: string; stepRuns: StepRun[] }>) {
            const { workflowRunId, stepRuns } = action.payload;
            if (!state.byWorkflowRunId[workflowRunId]) {
                state.byWorkflowRunId[workflowRunId] = {
                    stepRunsById: {},
                    stepGroupAutomationRunsByArchetype: {},
                };
            }
            const map = state.byWorkflowRunId[workflowRunId].stepRunsById;
            for (const sr of stepRuns) {
                map[sr.id] = pickLatestStepRun(map[sr.id], sr);
            }
        },
        updateStepRun(state, action: PayloadAction<StepRun>) {
            const stepRun = action.payload;
            for (const wfr of Object.values(state.byWorkflowRunId)) {
                if (wfr.stepRunsById[stepRun.id]) {
                    wfr.stepRunsById[stepRun.id] = pickLatestStepRun(wfr.stepRunsById[stepRun.id], stepRun);
                }
            }
        },
        upsertStepGroupAutomationRuns(
            state,
            action: PayloadAction<{
                workflowRunId: string;
                archetype: ReleaseStepType;
                automationRuns: AutomationRun[];
            }>,
        ) {
            const { workflowRunId, archetype, automationRuns } = action.payload;
            if (!state.byWorkflowRunId[workflowRunId]) {
                state.byWorkflowRunId[workflowRunId] = {
                    stepRunsById: {},
                    stepGroupAutomationRunsByArchetype: {},
                };
            }
            state.byWorkflowRunId[workflowRunId].stepGroupAutomationRunsByArchetype[archetype] = automationRuns;
        },
    },
});

export const stepRunsSummaryActions = stepRunsSummarySlice.actions;

export default stepRunsSummarySlice.reducer;
